import AddButton from '@components/common/AddButton';
import ButtonT from '@components/tailwind-components/Button/ButtonT';
import { NEW_CUSTOMER_ROUTE, PROVISION_STATUS_ROUTE } from '@constants/routes';
import { useOnce } from '@conversed/ui/src/hooks/useOnce';
import { useLogin } from '@hooks/auth/useLogin';
import { useCompanySelector } from '@hooks/useCompanySelector';
import { useMainSelector } from '@hooks/useMainSelector';
import conversedLogo from '@images/logo-black.svg';
import type { TCompany } from '@interfaces/IAuth';
import { mainActions } from '@store/reducers/mainHandler';
import clsx from 'clsx';
import type React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { generatePath, Link, useNavigate } from 'react-router';
import CompanyCard from '../company/CompanyCard';

export const CompanySelectorPage: React.FC = () => {
  const { t } = useTranslation();
  const { logout } = useLogin();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    hasUserCompanies,
    companies,
    setSelectedCompany,
    companyStateFilter,
    setCompanyStateFilter,
  } = useCompanySelector();
  const {
    user: { currentUser },
  } = useMainSelector();
  const isSuperAdmin = currentUser?.isSuperAdmin ?? false;

  // * A superAdmin clicking a company mid-provision, mid-teardown, or left in a
  // * failed state goes to its status curtain instead of dropping into a
  // * not-yet-ready app; every other case selects the company normally. The
  // * curtain is also the only retry surface for deprovision_failed, since such a
  // * company is inactive and the settings lifecycle modal is active-only.
  const handleSelectCompany = useCallback(
    (company: TCompany) => {
      const routesToProvision =
        isSuperAdmin &&
        (company.provisionStatus === 'in_progress' ||
          company.provisionStatus === 'failed' ||
          company.provisionStatus === 'deprovisioning' ||
          company.provisionStatus === 'deprovision_failed');

      if (routesToProvision) {
        navigate(
          generatePath(PROVISION_STATUS_ROUTE, { companyId: company.id }),
        );

        return;
      }

      setSelectedCompany(company);
    },
    [isSuperAdmin, navigate, setSelectedCompany],
  );

  const companyStateTabClasses =
    'px-2 py-1 font-light text-sm leading-1 flex items-center';
  const companyStateActiveTabClasses = 'bg-[#4CAF50] text-white';
  const companyStateInactiveTabClasses = 'bg-[#f0f0f0] text-[#999]';

  useOnce(() => {
    dispatch(
      mainActions.resetStates({
        shouldResetSelectedCompany: true,
      }),
    );
  });

  const getCompanyStateTabClasses = (state: string) => {
    return clsx(companyStateTabClasses, {
      [companyStateActiveTabClasses]: companyStateFilter === state,
      [companyStateInactiveTabClasses]: companyStateFilter !== state,
    });
  };

  return (
    <div className='w-full flex justify-center items-center flex-col h-dvh text-gray-600'>
      <div className='h-[calc(100%-50px)] w-full flex justify-center items-center'>
        <div className='w-full h-full max-w-[600px] flex justify-center flex-col items-center pt-[60px]'>
          <img src={conversedLogo} alt='company-logo' />

          {hasUserCompanies ? (
            <>
              <div className='text-primary-base h-[48px] mx-auto md:text-4xl font-medium md:mt-[60px] md:mb-[32px] text-3xl mt-[35px] mb-[12px]'>
                {t('company-selector.title')}
              </div>
              <div className='mr-4 ml-auto mb-1'>
                <div className='flex items-end justify-end cursor-pointer overflow-hidden rounded-md'>
                  <div
                    className={getCompanyStateTabClasses('all')}
                    onClick={() => setCompanyStateFilter('all')}>
                    {t('company-selector.company-state.all')}
                  </div>

                  <div
                    className={getCompanyStateTabClasses('active')}
                    onClick={() => setCompanyStateFilter('active')}>
                    {t('company-selector.company-state.active')}
                  </div>

                  <div
                    className={getCompanyStateTabClasses('inactive')}
                    onClick={() => setCompanyStateFilter('inactive')}>
                    {t('company-selector.company-state.inactive')}
                  </div>
                </div>
              </div>

              <div className='w-full h-full overflow-y-auto'>
                {companies.map((company) => (
                  <CompanyCard
                    key={company.identifier}
                    name={company.name}
                    iconUrl={company.icon}
                    studioType={company.studioType}
                    provisionStatus={
                      isSuperAdmin ? company.provisionStatus : undefined
                    }
                    onPress={() => handleSelectCompany(company)}
                  />
                ))}
              </div>
              {isSuperAdmin && (
                <div className='flex items-center justify-center p-2'>
                  <Link to={NEW_CUSTOMER_ROUTE}>
                    <AddButton />
                  </Link>
                </div>
              )}
            </>
          ) : (
            <>
              <div className='text-primary-base px-2 mx-auto text-center md:text-6xl font-medium md:mt-[75px] md:mb-[32px] text-3xl mt-[35px] mb-[12px]'>
                {t('company-selector.no-customer-error')}
              </div>
              <ButtonT
                type='button'
                styling='primary'
                size='md'
                rounding='md'
                onClick={() => {
                  logout();
                }}>
                {t('company-selector.logout')}
              </ButtonT>
            </>
          )}
        </div>
      </div>
      <div className='tailwind-copyright'>
        {t('general.copyright.label', {
          date: new Date().getFullYear().toString(),
        })}
      </div>
    </div>
  );
};
